// src/components/asset/ModuleLibraryView.jsx
import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip,
  TextField, InputAdornment, IconButton,
  Paper, Button, Tooltip, Divider,
  TablePagination, CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon, FilterList as FilterIcon,
  ViewModule as ViewModuleIcon, ViewList as ViewListIcon,
  Add as AddIcon, Visibility as VisibilityIcon,
  Category as CategoryIcon, Science as ScienceIcon,
  Power as PowerIcon, Scale as WeightIcon,
  Storage as StorageIcon, Build as BuildIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import Module3DViewer from './Module3DViewer';

const ROWS_PER_PAGE_OPTIONS = [12, 24, 48, 96];
const SYSTEM_OWNER = '0';

export default function ModuleLibraryView({
  modules,
  loading,
  onAddToScheme,
  onEditModule,
  onCreateModule,
  // === RBAC ===
  canCreate = false,                                  // «Создать модуль»
  canManageModule = () => false,                      // (module) => boolean — «Редактировать»
  canAddToScheme = false,                             // «Добавить в чертёж»
  myCharacterIds = [],
}) {
  const [viewMode, setViewMode] = useState('grid');
  const [selectedModule, setSelectedModule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPurpose, setFilterPurpose] = useState('all');
  const [filterTechLevel, setFilterTechLevel] = useState('all');
  const [show3DPreview, setShow3DPreview] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(24);

  const categories = useMemo(() => {
    const cats = new Set(modules.map((m) => m.category || 'internal'));
    return ['all', ...Array.from(cats)];
  }, [modules]);

  const purposes = useMemo(() => {
    const purps = new Set(modules.map((m) => m.purpose));
    return ['all', ...Array.from(purps)];
  }, [modules]);

  const techLevels = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];

  const filteredModules = useMemo(() => {
    return modules.filter((module) => {
      const matchesSearch =
        module.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        module.purpose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        module.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || module.category === filterCategory;
      const matchesPurpose = filterPurpose === 'all' || module.purpose === filterPurpose;
      const matchesTech =
        filterTechLevel === 'all' || module.tech_level === parseInt(filterTechLevel, 10);
      return matchesSearch && matchesCategory && matchesPurpose && matchesTech;
    });
  }, [modules, searchTerm, filterCategory, filterPurpose, filterTechLevel]);

  const handleFiltersChange = () => setPage(0);
  const totalCount = filteredModules.length;
  const paginatedModules = filteredModules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterCategory('all');
    setFilterPurpose('all');
    setFilterTechLevel('all');
    setPage(0);
  };
  const handleView3D = (module) => {
    setSelectedModule(module);
    setShow3DPreview(true);
  };

  const getModuleIcon = (purpose) => {
    const map = {
      energy: <PowerIcon />,
      propulsion: <BuildIcon />,
      weapon_system: <ScienceIcon />,
      cargo: <StorageIcon />,
      habitation: <ViewModuleIcon />,
    };
    return map[purpose] || <BuildIcon />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      internal: 'primary',
      external: 'secondary',
      military: 'error',
      power_systems: 'warning',
      resource_systems: 'success',
    };
    return colors[category] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // === Кнопки на карточке модуля: управление + «Добавить в чертёж» ===
  const moduleCardActions = (module) => {
    const canEdit = canManageModule(module);
    return (
      <Box sx={{ p: 1, pt: 0, display: 'flex', gap: 0.5 }}>
        {canAddToScheme && (
          <Button
            size="small"
            variant="contained"
            fullWidth
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => onAddToScheme(module)}
            sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 26 }}
          >
            Добавить
          </Button>
        )}
        {canEdit && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => onEditModule(module)}
            sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 26 }}
          >
            Edit
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ШАПКА + ФИЛЬТРЫ */}
      <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CategoryIcon color="primary" sx={{ fontSize: 22 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                Библиотека модулей
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {totalCount} из {modules.length} модулей
                {searchTerm && ` • найдено: ${totalCount}`}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canCreate && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon fontSize="small" />}
                onClick={onCreateModule}
                sx={{ fontSize: '0.7rem', py: 0.25, minHeight: 26 }}
              >
                Создать модуль
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            >
              {viewMode === 'grid' ? <ViewListIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={0.75}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Поиск..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                handleFiltersChange();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        handleFiltersChange();
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { fontSize: '0.75rem' },
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              select
              fullWidth
              label="Категория"
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                handleFiltersChange();
              }}
              size="small"
              SelectProps={{ native: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
            >
              <option value="all">Все</option>
              {categories.filter((c) => c !== 'all').map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              select
              fullWidth
              label="Назначение"
              value={filterPurpose}
              onChange={(e) => {
                setFilterPurpose(e.target.value);
                handleFiltersChange();
              }}
              size="small"
              SelectProps={{ native: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
            >
              <option value="all">Все</option>
              {purposes.filter((p) => p !== 'all').map((purp) => (
                <option key={purp} value={purp}>{purp}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              select
              fullWidth
              label="TL"
              value={filterTechLevel}
              onChange={(e) => {
                setFilterTechLevel(e.target.value);
                handleFiltersChange();
              }}
              size="small"
              SelectProps={{ native: true }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
            >
              <option value="all">Все</option>
              {techLevels.map((tl) => (
                <option key={tl} value={tl}>T{tl}</option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon fontSize="small" />}
              onClick={handleResetFilters}
              sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 26 }}
            >
              Сброс
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ГРИД / СПИСОК */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredModules.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              Модули не найдены
            </Typography>
          </Box>
        ) : viewMode === 'grid' ? (
          <>
            <Grid container spacing={1} sx={{ p: 1 }}>
              {paginatedModules.map((module) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={module.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 110,
                        bgcolor: 'background.default',
                        position: 'relative',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleView3D(module)}
                    >
                      {module.visual_representation ? (
                        <Module3DViewer
                          module={module}
                          width="100%"
                          height="100%"
                          interactive={false}
                          autoRotate
                        />
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary' }}>
                          {getModuleIcon(module.purpose)}
                        </Box>
                      )}
                      <Tooltip title="3D просмотр">
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'primary.main', color: 'white' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView3D(module);
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <CardContent sx={{ flex: 1, p: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 0.5 }}>
                        <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 600 }}>
                          {module.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`T${module.tech_level}`}
                          color={module.tech_level >= 8 ? 'primary' : module.tech_level >= 5 ? 'default' : 'secondary'}
                          sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.55rem', px: 0.5 } }}
                        />
                      </Box>

                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.6rem' }}>
                        {module.purpose} • {module.size_tier}
                      </Typography>

                      <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={module.category}
                          color={getCategoryColor(module.category)}
                          variant="outlined"
                          sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }}
                        />
                        {module.has_interior && (
                          <Chip size="small" label="🚪" variant="outlined" sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }} />
                        )}
                      </Box>

                      <Divider sx={{ my: 0.5 }} />

                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {module.mass_kg > 0 && (
                          <Tooltip title="Масса">
                            <Chip
                              size="small"
                              icon={<WeightIcon sx={{ fontSize: 10 }} />}
                              label={`${(module.mass_kg / 1000).toFixed(1)}т`}
                              sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }}
                            />
                          </Tooltip>
                        )}
                        {module.power_draw > 0 && (
                          <Chip
                            size="small"
                            color="error"
                            label={`-${module.power_draw}`}
                            sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }}
                          />
                        )}
                        {module.power_output > 0 && (
                          <Chip
                            size="small"
                            color="success"
                            label={`+${module.power_output}`}
                            sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }}
                          />
                        )}
                      </Box>
                    </CardContent>

                    {moduleCardActions(module)}
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Paper variant="outlined" sx={{ mx: 1, mb: 1 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelRowsPerPage="Модулей:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                sx={{
                  minHeight: 32,
                  '& .MuiTablePagination-toolbar': { minHeight: 32 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.7rem' },
                }}
              />
            </Paper>
          </>
        ) : (
          <>
            <Box sx={{ p: 1 }}>
              {paginatedModules.map((module) => (
                <Paper key={module.id} variant="outlined" sx={{ p: 1, mb: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box sx={{ width: 80, height: 60, bgcolor: 'background.default', flexShrink: 0 }}>
                    {module.visual_representation && (
                      <Module3DViewer module={module} width="100%" height="100%" interactive={false} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }} noWrap>
                      {module.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.6rem' }}>
                      {module.purpose} • {module.category} • {module.size_tier} • T{module.tech_level}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                      <Chip size="small" label={`${(module.mass_kg / 1000).toFixed(1)}т`} sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }} />
                      <Chip size="small" label={`${module.power_draw}кВт`} sx={{ height: 14, '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 } }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" variant="outlined" onClick={() => handleView3D(module)} sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 22 }}>
                      3D
                    </Button>
                    {canAddToScheme && (
                      <Button size="small" variant="contained" onClick={() => onAddToScheme(module)} sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 22 }}>
                        Добавить
                      </Button>
                    )}
                    {canManageModule(module) && (
                      <Button size="small" variant="outlined" onClick={() => onEditModule(module)} sx={{ fontSize: '0.65rem', py: 0.25, minHeight: 22 }}>
                        Edit
                      </Button>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper variant="outlined" sx={{ mx: 1, mb: 1 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelRowsPerPage="Модулей:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                sx={{
                  minHeight: 32,
                  '& .MuiTablePagination-toolbar': { minHeight: 32 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.7rem' },
                }}
              />
            </Paper>
          </>
        )}
      </Box>

      {show3DPreview && selectedModule && (
        <Module3DViewer
          module={selectedModule}
          width="100%"
          height="600px"
          interactive
          autoRotate
          showInfo
          onClose={() => setShow3DPreview(false)}
        />
      )}
    </Box>
  );
}
